/**
 * AirportPicker — popover-wrapped airport selector for EditablePillRow.
 *
 * Wraps the existing `AirportSelect` (which is a Command-based combobox
 * backed by the `airport_codes` table). Maps the combobox's bare-code
 * value to the hook's `AirportRef` shape, looking up the display label
 * via `getAirportInfo` (direct `.eq('code', X)` lookup — unambiguous and
 * avoids the fuzzy-`ilike` false-positive risk in `searchAirportCodes`).
 *
 * Commit pattern: the bare `{ code }` is committed *synchronously* on
 * select so the pill UI updates immediately. The label is fetched in a
 * follow-up effect and patched in, guarded by an `active` flag so a
 * stale lookup from a previously-cancelled selection can't overwrite
 * the current value.
 */

import { useEffect, useRef } from 'react'
import { AirportSelect } from '@/components/ui/airport-select'
import { getAirportInfo } from '@/lib/airport-codes'
import type { AirportRef } from '@/lib/search/types'

interface AirportPickerProps {
  value?: AirportRef
  onChange: (next: AirportRef | undefined) => void
}

export function AirportPicker({ value, onChange }: AirportPickerProps) {
  // Track the most recent code we've kicked a lookup for so a late-arriving
  // resolve for a previously-selected code can't clobber a newer selection.
  const latestCodeRef = useRef<string | undefined>(value?.code)

  useEffect(() => {
    const code = value?.code
    latestCodeRef.current = code
    if (!code || value?.label) return
    let active = true
    void getAirportInfo(code).then((info) => {
      if (!active) return
      if (latestCodeRef.current !== code) return
      if (!info) return
      onChange({ code, label: `${info.city}, ${info.country} (${info.code})` })
    })
    return () => {
      active = false
    }
  }, [value?.code, value?.label, onChange])

  return (
    <div className="sc-picker" data-airport-picker>
      <AirportSelect
        value={value?.code ?? ''}
        onChange={(code) => {
          if (!code) {
            onChange(undefined)
            return
          }
          // Commit code synchronously; the useEffect above resolves the label.
          onChange({ code })
        }}
        placeholder="Search city or airport…"
      />
      {value?.code ? (
        <div className="sc-picker-row">
          <span>Selected</span>
          <span className="sub">{value.label ?? value.code}</span>
        </div>
      ) : null}
    </div>
  )
}
